import { useCallback, useEffect, useState } from 'react'
import { useNavigate, useSearch } from '@tanstack/react-router'

import { logger } from '@/utils/logger'

const log = logger.child({ file: 'src/hooks/usePresentationState.ts' })

export const usePresentationState = ({
  totalSlides,
  lessonId,
}: {
  totalSlides: number
  lessonId: string
}) => {
  const { slide = 1 } = useSearch({
    from: '/_authRoutes/lesson/$lessonId/presentation',
  })
  const navigate = useNavigate()
  const [isPresentationActive, setIsPresentationActive] = useState(false)
  const [syncStatus, setSyncStatus] = useState<'synced' | 'syncing' | 'error'>(
    'synced',
  )
  const [audienceConnected, setAudienceConnected] = useState(false)

  // Check if we have an audience view window on mount
  useEffect(() => {
    if (window.opener && !window.opener.closed) {
      setIsPresentationActive(true)
      setAudienceConnected(true)
    }
  }, [])

  const setCurrentSlide = useCallback(
    (newSlide: number) => {
      navigate({
        to: '.',
        params: { lessonId },
        search: { slide: newSlide },
        replace: true,
      })
    },
    [lessonId, navigate],
  )
  const nextSlide = useCallback(() => {
    const newSlide = Math.min(slide + 1, totalSlides)
    setCurrentSlide(newSlide)
    return newSlide
  }, [slide, totalSlides, setCurrentSlide])

  const prevSlide = useCallback(() => {
    const newSlide = Math.max(slide - 1, 1)
    setCurrentSlide(newSlide)
    return newSlide
  }, [setCurrentSlide, slide])

  const goToSlide = useCallback(
    (slideNumber: number) => {
      const newSlide = Math.max(1, Math.min(slideNumber, totalSlides))
      setCurrentSlide(newSlide)
      return newSlide
    },
    [totalSlides, setCurrentSlide],
  )

  const startPresentation = useCallback(() => {
    // If we don't have an opener window, open the audience view
    if (!window.opener || window.opener.closed) {
      const audienceUrl = `${window.location.origin}/audience?slide=${slide}`
      window.open(
        audienceUrl,
        'audience-view',
        'width=1920,height=1080,fullscreen=yes,scrollbars=no,resizable=yes',
      )
    }
    setIsPresentationActive(true)
  }, [slide])

  const stopPresentation = useCallback(async () => {
    // Send stop message to audience view before closing
    if (window.opener && !window.opener.closed) {
      try {
        window.opener.postMessage(
          {
            type: 'presentation-stop',
          },
          '*',
        )
        log.info('🛑 Presenter: Sent presentation-stop message to audience')
      } catch (error) {
        log.error(
          { error },
          '❌ Presenter: Could not send stop message to audience',
        )
      }
    }

    // wait till navigated to an unauthenticated route to not trigger any auth functions
    await navigate({
      to: '/login',
      replace: true,
    })

    // Close the presenter window
    window.close()
  }, [navigate])

  return {
    currentSlide: slide,
    setCurrentSlide,
    isPresentationActive,
    syncStatus,
    setSyncStatus,
    audienceConnected,
    setAudienceConnected,
    nextSlide,
    prevSlide,
    goToSlide,
    startPresentation,
    stopPresentation,
  }
}
